import { PropsWithChildren } from "react";
import type { RecurlyBillingInfo } from "@inferno/renderer-shared-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import { CoastAlert } from "../common/coast-alert/CoastAlert.model";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";

interface CoastUserBillingInfoDisplayProps {
  billingInfo?: RecurlyBillingInfo | null;
  fetchingError?: CoastAlert | null;
  onEditClick: () => void;
  isSubMenu: boolean;
}

const CoastUserBillingInfoDisplayBase = ({
  onEditClick,
  billingInfo,
  isSubMenu,
  children,
}: PropsWithChildren<CoastUserBillingInfoDisplayProps>) => (
  <section className="billing-section" hidden={false}>
    <div className="content">
      <Heading level={3}>{isSubMenu ? "Billing Information" : "Payment Details"}</Heading>
      {children}
    </div>

    <Button
      click={() => billingInfo && onEditClick()}
      kind={ButtonKind.SECONDARY}
      className="account-button"
      disabled={!billingInfo}
    >
      Edit
    </Button>
  </section>
);

export function CoastUserBillingInfoDisplay({
  billingInfo,
  isSubMenu,
  fetchingError,
  onEditClick,
}: CoastUserBillingInfoDisplayProps) {
  if (!billingInfo) {
    return (
      <CoastUserBillingInfoDisplayBase billingInfo={billingInfo} isSubMenu={isSubMenu} onEditClick={onEditClick}>
        <CoastAlertDisplayError
          message={
            fetchingError?.message || "There was an error loading your billing information. Please try again later."
          }
          isDisplayed={!billingInfo}
        />
      </CoastUserBillingInfoDisplayBase>
    );
  }

  const address = `${billingInfo.address.street1} ${billingInfo.address.city}`;
  const account = billingInfo.paymentMethod.lastFour ? `ending ${billingInfo.paymentMethod.lastFour}` : "";
  const name = billingInfo.paymentMethod.nameOnAccount || `${billingInfo.firstName} ${billingInfo.lastName}`;

  let paymentTypeProvider = "Account";

  if (billingInfo.paymentMethod) {
    const paymentTypeProviderCandidate =
      billingInfo.paymentMethod.object !== "credit_card"
        ? billingInfo.paymentMethod.routingNumberBank
        : billingInfo.paymentMethod.cardType;

    if (paymentTypeProviderCandidate) {
      paymentTypeProvider = paymentTypeProviderCandidate;
    }
  }

  return (
    <CoastUserBillingInfoDisplayBase billingInfo={billingInfo} isSubMenu={isSubMenu} onEditClick={onEditClick}>
      <p>
        <strong>Name: </strong>
        {name}
      </p>
      <p>
        <strong>Address: </strong>
        {address}
      </p>
      <p>
        <strong>Payment Method: </strong>
        {paymentTypeProvider} {account}
      </p>
    </CoastUserBillingInfoDisplayBase>
  );
}
